import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

const SupabaseDebug = () => {
  const { user, isLoading: authLoading } = useAuth()
  const [results, setResults] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testPassword, setTestPassword] = useState('')

  const addResult = (test: string, result: any) => {
    setResults(prev => [...prev, { test, result, timestamp: new Date().toISOString() }])
  }

  const runTests = async () => {
    setIsLoading(true)
    setResults([])

    try {
      // Test 1: Check if Supabase client exists
      addResult('Supabase Client', supabase ? 'Connected' : 'Not connected')

      if (!supabase) {
        setIsLoading(false)
        return
      }

      // Test 2: Check current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      addResult('Current Session', { 
        user: sessionData.session?.user?.email || 'No user',
        userId: sessionData.session?.user?.id,
        emailConfirmed: sessionData.session?.user?.email_confirmed_at,
        sessionExists: !!sessionData.session,
        error: sessionError?.message 
      })

      // Test 3: Test profiles table access (count)
      const { data: countData, error: countError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
      
      addResult('Profiles Table Count', { 
        count: countData, 
        error: countError?.message,
        errorCode: countError?.code 
      })

      // Test 4: Test profiles table select
      const { data: selectData, error: selectError } = await supabase
        .from('profiles')
        .select('*')
        .limit(5)
      
      addResult('Profiles Table Select', { 
        data: selectData, 
        error: selectError?.message,
        errorCode: selectError?.code 
      })

      // Test 5: If user is logged in, try to get their profile
      if (sessionData.session?.user) {
        const { data: userProfile, error: userError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', sessionData.session.user.id)
          .single()
        
        addResult('User Profile Lookup', { 
          profile: userProfile, 
          error: userError?.message,
          errorCode: userError?.code 
        })
      }

    } catch (error) {
      addResult('Test Error', error)
    }

    setIsLoading(false)
  }

  const testLogin = async () => {
    if (!testEmail || !testPassword) {
      addResult('Test Login', { error: 'Please enter email and password' })
      return
    }

    setIsLoading(true)
    try {
      const { data, error } = await supabase!.auth.signInWithPassword({
        email: testEmail,
        password: testPassword
      })

      addResult('Test Login', {
        success: !error,
        user: data.user?.email,
        session: !!data.session,
        emailConfirmed: data.user?.email_confirmed_at,
        error: error?.message
      })

      // After successful login, immediately check session again
      if (!error && data.session) {
        setTimeout(async () => {
          const { data: sessionCheck } = await supabase!.auth.getSession()
          addResult('Session Check After Login', {
            sessionExists: !!sessionCheck.session,
            user: sessionCheck.session?.user?.email,
            sameSession: sessionCheck.session?.access_token === data.session?.access_token
          })
        }, 1000)
      }
    } catch (error) {
      addResult('Test Login', { error: error })
    }
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen relative overflow-hidden p-8">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="glass-mockup rounded-3xl p-8">
          <h1 className="text-3xl font-bold text-white mb-6">Supabase Debug Console</h1>
          
          <div className="glass-badge rounded-xl p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">AuthContext Status</h3>
            <div className="text-gray-300 text-sm">
              <p>User: {user ? user.email : 'Not logged in'}</p>
              <p>Loading: {authLoading ? 'Yes' : 'No'}</p>
            </div>
          </div>
          
          <div className="flex gap-4 mb-6">
            <button
              onClick={runTests}
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Running Tests...' : 'Run Supabase Tests'}
            </button>
          </div>

          <div className="glass-badge rounded-xl p-4 mb-6">
            <h3 className="text-white font-semibold mb-4">Test Login</h3>
            <div className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
              />
              <input
                type="password"
                placeholder="Password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
              />
              <button
                onClick={testLogin}
                disabled={isLoading}
                className="btn-secondary"
              >
                Test Login
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {results.map((result, index) => (
              <div key={index} className="glass-badge rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2">{result.test}</h3>
                <pre className="text-gray-300 text-sm overflow-auto bg-black/30 p-3 rounded">
                  {JSON.stringify(result.result, null, 2)}
                </pre>
                <p className="text-gray-500 text-xs mt-2">{result.timestamp}</p>
              </div>
            ))}
          </div>

          {results.length === 0 && !isLoading && (
            <div className="text-center text-gray-400 py-8">
              Click "Run Supabase Tests" to diagnose connection issues
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupabaseDebug 